import { Paper, Table, TableCell, TableContainer, TableRow, TableBody, Typography, useTheme, Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, CircularProgress } from '@mui/material';
import { CustomTableFoot, CustomTableHead, CustomTableHeadCell, CustomTableRow } from '../../components/@extended/CustomTable';
import { useParams } from 'react-router-dom';
import { useGetAllSponserConfigurationsQuery, useGetTrailSysQuery, useDeleteTrialCompletelyMutation } from '../../store/slices/apiSlice';
import { Columns3 } from './TrailDetailsTableColumns';
import RightArrow from '../../components/icons/RightArrow';
import CustomButton from '../../components/@extended/CustomButton';
import { useNavigate } from 'react-router-dom';
import CustomTablePagination from '../../components/common/TablePagination';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsSystemAdmin } from '../../store/slices/authSlice';
import { updatePagination } from '../../store/slices/applicationSlice';
import { openMessage } from '../../store/slices/showMessageSlice';
import CustomCard from '../../components/@extended/CustomCard';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';


const TrailDetailsTable = () => {
  const theme = useTheme();
  const navigate=useNavigate();
  const dispatch = useDispatch();
  const columns = Columns3();
  const { sponsorId } = useParams();
  const { data: sponserConfigurations } = useGetAllSponserConfigurationsQuery(sponsorId);
  const {page,rowsPerPage} = useSelector((state) => state.application.pagination.TRAIL_DETAILS_TABLE);
  const { data: data1, isLoading, refetch } = useGetTrailSysQuery(sponsorId);
  
  // Check if current user is system admin
  const isSystemAdmin = useSelector(selectIsSystemAdmin);
  
  // Delete trial mutation
  const [deleteTrialCompletely, { isLoading: isDeleting }] = useDeleteTrialCompletelyMutation();
  
  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [trialToDelete, setTrialToDelete] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  
  if (isLoading) {
    return <></>;
  }

  const onNewPage = (page, rowsPerPage) =>{
    dispatch(updatePagination({tableType:'TRAIL_DETAILS_TABLE',page,rowsPerPage}));
  }

  const handleCreate = ()=>{
    if(sponserConfigurations && sponserConfigurations.length >0 ){
      navigate(`/sponsor/createTrial/${sponsorId}`)
    }else{
      dispatch(openMessage({ messageSeverity: 'error', message: 'At least one configuration need to assign to sponser.' }));
    }
  }
  
  // Handle opening delete confirmation dialog
  const handleDeleteClick = (trial) => {
    setTrialToDelete(trial);
    setConfirmText('');
    setDeleteDialogOpen(true);
  };
  
  // Handle closing delete dialog
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setTrialToDelete(null);
    setConfirmText('');
  };
  
  // Handle confirming delete
  const handleConfirmDelete = async () => {
    if (!trialToDelete || confirmText !== 'DELETE') {
      dispatch(openMessage({ messageSeverity: 'error', message: 'Please type DELETE to confirm.' }));
      return;
    }
    
    try {
      const result = await deleteTrialCompletely({
        sponsorId: sponsorId,
        trialId: trialToDelete.id,
      }).unwrap();
      
      dispatch(openMessage({ 
        messageSeverity: 'success', 
        message: result.message || 'Trial deleted successfully.' 
      }));
      
      handleDeleteDialogClose();
      refetch(); // Refresh the trials list
    } catch (error) {
      console.error('Failed to delete trial:', error);
      dispatch(openMessage({ 
        messageSeverity: 'error', 
        message: error?.data?.message || error?.message || 'Failed to delete trial. Please try again.' 
      }));
    }
  };

  // Calculate columns with optional action column for system admin
  const displayColumns = isSystemAdmin 
    ? [...columns, { id: 'action', label: 'Actions', style: { minWidth: 100 }, align: 'center' }]
    : columns;

  return (
    <>
      <CustomCard
        title={
          <Typography variant="h6" fontWeight={600} color={theme.palette.grey[800]}>
            Trials
          </Typography>
        }
        //   action={
        // <CustomButton variant='contained' onClick={handleCreate}>
        //     + Create Trial
        //   </CustomButton>
        //   }
      >
        <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Table stickyHeader aria-label="sticky table">
            <CustomTableHead>
              <TableRow>
                {displayColumns.map((column) => (
                  <CustomTableHeadCell key={column.id} align={column.align} sx={{ ...column.style }}>
                    {column.label}
                  </CustomTableHeadCell>
                ))}
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {data1 && data1.length > 0 ? (
                (rowsPerPage > 0 ? data1.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : data1).map(
                  (row) => (
                    <CustomTableRow key={row?.id} hover role="checkbox" tabIndex={-1}>
                      <TableCell sx={{ maxWidth: 350 }}>
                        <Typography align="justify">{row?.trialTitle}</Typography>
                      </TableCell>
                      <TableCell>{row?.protocolNumber}</TableCell>
                      <TableCell align="center">{row?.protocolVersion}</TableCell>
                      <TableCell align="center">{row?.totalSubjectNumber}</TableCell>
                      {isSystemAdmin && (
                        <TableCell align="center">
                          <Tooltip title="Permanently Delete Trial (System Admin Only)">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(row)}
                              disabled={isDeleting}
                            >
                              <DeleteForeverIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </CustomTableRow>
                  )
                )
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={displayColumns.length}>
                    <Typography variant="subtitle1" color="initial">
                      No Trail Found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <CustomTableFoot>
              <CustomTableRow sx={{ right: 0 }}>
                <CustomTableHeadCell colSpan={displayColumns.length}>
                  <CustomTablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    count={data1?.length}
                    onNewPage={onNewPage}
                  />
                </CustomTableHeadCell>
              </CustomTableRow>
            </CustomTableFoot>
          </Table>
        </TableContainer>
      </CustomCard>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: theme.palette.error.main, fontWeight: 600 }}>
          ⚠️ Permanent Trial Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You are about to <strong>permanently delete</strong> the following trial and <strong>ALL</strong> related data:
          </DialogContentText>
          
          {trialToDelete && (
            <Box sx={{ 
              backgroundColor: theme.palette.grey[100], 
              p: 2, 
              borderRadius: 1, 
              mb: 2 
            }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {trialToDelete.trialTitle}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Protocol: {trialToDelete.protocolNumber} (v{trialToDelete.protocolVersion})
              </Typography>
            </Box>
          )}
          
          <DialogContentText sx={{ mb: 2, color: theme.palette.error.main }}>
            <strong>This action will permanently delete:</strong>
            <ul style={{ margin: '8px 0' }}>
              <li>All DOA tasks and logs</li>
              <li>All site trials and site initiation data</li>
              <li>All user roles for this trial</li>
              <li>All documents, bank details, and cost data</li>
              <li>All configuration mappings</li>
            </ul>
            <strong>This action cannot be undone!</strong>
          </DialogContentText>
          
          <DialogContentText sx={{ mb: 1 }}>
            To confirm, please type <strong>DELETE</strong> below:
          </DialogContentText>
          
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: `2px solid ${confirmText === 'DELETE' ? theme.palette.success.main : theme.palette.grey[300]}`,
              borderRadius: '4px',
              outline: 'none',
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleDeleteDialogClose} 
            variant="outlined"
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={confirmText !== 'DELETE' || isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteForeverIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TrailDetailsTable;
